import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Database,
  Download,
  Upload,
  Trash2,
  RefreshCw,
  Clock,
  HardDrive,
  Calendar,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Play,
  Archive,
  Loader2,
  FileArchive,
  History,
  Settings,
  Shield,
} from "lucide-react";
import AdminSidebar from "../components/layout/AdminSidebar";

const BackupsPage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Backups list
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  
  // Collections for selective restore
  const [collections, setCollections] = useState([]);
  
  // Restore history
  const [restoreHistory, setRestoreHistory] = useState([]);
  
  // Create backup modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createDescription, setCreateDescription] = useState("");
  const [includeUploads, setIncludeUploads] = useState(true);
  const [compressBackup, setCompressBackup] = useState(true);
  const [creating, setCreating] = useState(false);
  
  // Restore modal
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [restoreType, setRestoreType] = useState("full");
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [restoreUploads, setRestoreUploads] = useState(true);
  const [restoring, setRestoring] = useState(false);
  
  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [backupToDelete, setBackupToDelete] = useState(null);
  const [hardDelete, setHardDelete] = useState(false);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/backups/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, [token]);

  // Fetch backups list
  const fetchBackups = useCallback(async () => {
    setBackupsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/backups?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBackups(response.data.backups || []);
    } catch (error) {
      toast.error("Failed to load backups");
    } finally {
      setBackupsLoading(false);
    }
  }, [token]);

  // Fetch collections
  const fetchCollections = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/backups/collections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCollections(response.data.collections || []);
    } catch (error) {
      console.error("Failed to fetch collections:", error);
    }
  }, [token]);

  // Fetch restore history
  const fetchRestoreHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/backups/restore-history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRestoreHistory(response.data.restore_logs || []);
    } catch (error) {
      console.error("Failed to fetch restore history:", error);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchBackups(), fetchCollections()]);
      setLoading(false);
    };
    init();
  }, [fetchStats, fetchBackups, fetchCollections]);

  // Create backup
  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      const response = await axios.post(
        `${API}/admin/backups/create`,
        {
          description: createDescription,
          include_uploads: includeUploads,
          compress: compressBackup,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Backup created: ${response.data.backup_id}`);
      setCreateModalOpen(false);
      setCreateDescription("");
      fetchBackups();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create backup");
    } finally {
      setCreating(false);
    }
  };

  // Trigger nightly backup
  const handleTriggerNightly = async () => {
    if (!window.confirm("Run the full nightly backup job? This includes backup + cleanup + AI log archival.")) {
      return;
    }
    
    try {
      toast.info("Starting nightly backup job...");
      const response = await axios.post(
        `${API}/admin/backups/trigger-nightly`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Nightly backup completed successfully");
      fetchBackups();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Nightly backup failed");
    }
  };

  // Open restore modal
  const openRestoreModal = (backup) => {
    setSelectedBackup(backup);
    setRestoreType("full");
    setSelectedCollections([]);
    setRestoreUploads(true);
    setRestoreModalOpen(true);
  };

  // Restore backup
  const handleRestore = async () => {
    if (!window.confirm("WARNING: This will overwrite existing data! Are you sure you want to restore?")) {
      return;
    }
    
    setRestoring(true);
    try {
      const response = await axios.post(
        `${API}/admin/backups/${selectedBackup.backup_id}/restore`,
        {
          restore_type: restoreType,
          collections: restoreType === "selective" ? selectedCollections : null,
          restore_uploads: restoreUploads,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Restore completed: ${response.data.total_collections} collections restored`);
      setRestoreModalOpen(false);
      fetchRestoreHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  // Download backup
  const handleDownload = async (backup) => {
    try {
      const response = await axios.get(
        `${API}/admin/backups/${backup.backup_id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${backup.backup_id}.tar.gz`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download started");
    } catch (error) {
      toast.error("Download failed");
    }
  };

  // Delete backup
  const handleDelete = async () => {
    try {
      await axios.delete(
        `${API}/admin/backups/${backupToDelete.backup_id}?hard_delete=${hardDelete}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Backup deleted");
      setDeleteModalOpen(false);
      setBackupToDelete(null);
      setHardDelete(false);
      fetchBackups();
      fetchStats();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  // Cleanup old backups
  const handleCleanup = async () => {
    if (!window.confirm("Delete all backups older than 30 days?")) return;
    
    try {
      const response = await axios.post(
        `${API}/admin/backups/cleanup?days_to_keep=30`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      fetchBackups();
      fetchStats();
    } catch (error) {
      toast.error("Cleanup failed");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    while (bytes >= 1024 && i < units.length - 1) {
      bytes /= 1024;
      i++;
    }
    return `${bytes.toFixed(1)} ${units[i]}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case "completed_with_errors":
        return <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" />With Errors</Badge>;
      case "pending":
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case "failed":
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <AdminSidebar>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
      <div className="min-h-screen bg-background" data-testid="backups-page">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database className="w-6 h-6 text-primary" />
              <div>
                <h1 className="font-serif text-xl font-semibold">Backup & Restore</h1>
                <p className="text-sm text-muted-foreground">Automated nightly backups with restore capability</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleTriggerNightly}
                data-testid="trigger-nightly-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Run Nightly Job
              </Button>
              <Button
                size="sm"
                onClick={() => setCreateModalOpen(true)}
                data-testid="create-backup-btn"
              >
                <Archive className="w-4 h-4 mr-2" />
                Create Backup
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" className="gap-2">
              <HardDrive className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="backups" className="gap-2">
              <FileArchive className="w-4 h-4" />
              Backups
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="w-4 h-4" />
              Restore History
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {stats && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100">
                          <FileArchive className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{stats.total_backups}</p>
                          <p className="text-xs text-muted-foreground">Total Backups</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-green-100">
                          <HardDrive className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{stats.total_size_mb} MB</p>
                          <p className="text-xs text-muted-foreground">Total Size</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-amber-100">
                          <Calendar className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{stats.retention_days}</p>
                          <p className="text-xs text-muted-foreground">Retention Days</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-purple-100">
                          <Clock className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">2:00 AM UTC</p>
                          <p className="text-xs text-muted-foreground">Nightly Schedule</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Latest Backup Card */}
                {stats.latest_backup && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Latest Backup
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">Backup ID</p>
                          <p className="font-medium">{stats.latest_backup.backup_id}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Created At</p>
                          <p className="font-medium">{formatDate(stats.latest_backup.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Status</p>
                          {getStatusBadge(stats.latest_backup.status)}
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Documents</p>
                          <p className="font-medium">{stats.latest_backup.total_documents?.toLocaleString()}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Backup Schedule Info */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Backup Configuration
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Schedule</span>
                          <span className="text-sm font-medium">Every day at 2:00 AM UTC</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Retention</span>
                          <span className="text-sm font-medium">{stats.retention_days} days</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Max Backups</span>
                          <span className="text-sm font-medium">{stats.max_backups}</span>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Includes</span>
                          <span className="text-sm font-medium">All Collections + Uploads</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Compression</span>
                          <span className="text-sm font-medium">gzip (.tar.gz)</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">AI Log Archival</span>
                          <span className="text-sm font-medium">120 days → Archive</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Backups List Tab */}
          <TabsContent value="backups">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  {backups.length} backups available
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={fetchBackups}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleCleanup}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Cleanup Old
                  </Button>
                </div>
              </div>

              {backupsLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : backups.length > 0 ? (
                <div className="space-y-3">
                  {backups.map((backup) => (
                    <Card key={backup.backup_id} data-testid={`backup-${backup.backup_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{backup.backup_id}</span>
                              {getStatusBadge(backup.status)}
                              {backup.compressed && (
                                <Badge variant="outline">Compressed</Badge>
                              )}
                              {!backup.file_exists && (
                                <Badge variant="destructive">File Missing</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {backup.description || "No description"}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span>{formatDate(backup.created_at)}</span>
                              <span>{backup.total_documents?.toLocaleString()} docs</span>
                              <span>{formatSize(backup.compressed_size_bytes || backup.total_data_size_bytes)}</span>
                              <span>by {backup.triggered_by}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {backup.file_exists && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDownload(backup)}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openRestoreModal(backup)}
                                >
                                  <Upload className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setBackupToDelete(backup);
                                setDeleteModalOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <FileArchive className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">No backups found</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Restore History Tab */}
          <TabsContent value="history">
            <div className="space-y-4">
              <Button variant="outline" size="sm" onClick={fetchRestoreHistory}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>

              {restoreHistory.length > 0 ? (
                <div className="space-y-3">
                  {restoreHistory.map((restore, index) => (
                    <Card key={index}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">Restored from: {restore.backup_id}</span>
                              {getStatusBadge(restore.status)}
                            </div>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span>{formatDate(restore.completed_at)}</span>
                              <span>{restore.total_collections} collections</span>
                              <span>{restore.uploads_restored} files</span>
                              <span>by {restore.triggered_by}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <History className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">No restore operations yet</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Backup Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Backup</DialogTitle>
            <DialogDescription>
              Create a new backup of all data and uploaded files.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Description (optional)</Label>
              <Input
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                placeholder="e.g., Before major update"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="include-uploads"
                checked={includeUploads}
                onCheckedChange={setIncludeUploads}
              />
              <Label htmlFor="include-uploads">Include uploaded files</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="compress"
                checked={compressBackup}
                onCheckedChange={setCompressBackup}
              />
              <Label htmlFor="compress">Compress backup (.tar.gz)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateBackup} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Backup"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Modal */}
      <Dialog open={restoreModalOpen} onOpenChange={setRestoreModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Restore Backup
            </DialogTitle>
            <DialogDescription>
              WARNING: This will overwrite existing data. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedBackup && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium">{selectedBackup.backup_id}</p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(selectedBackup.created_at)} • {selectedBackup.total_documents} documents
                </p>
              </div>
              
              <div>
                <Label>Restore Type</Label>
                <Select value={restoreType} onValueChange={setRestoreType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full Restore (all collections)</SelectItem>
                    <SelectItem value="selective">Selective (choose collections)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {restoreType === "selective" && (
                <div>
                  <Label>Select Collections</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto">
                    {collections.map((col) => (
                      <div key={col.name} className="flex items-center gap-2">
                        <Checkbox
                          id={col.name}
                          checked={selectedCollections.includes(col.name)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedCollections([...selectedCollections, col.name]);
                            } else {
                              setSelectedCollections(selectedCollections.filter((c) => c !== col.name));
                            }
                          }}
                        />
                        <Label htmlFor={col.name} className="text-sm">
                          {col.name} ({col.document_count})
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Checkbox
                  id="restore-uploads"
                  checked={restoreUploads}
                  onCheckedChange={setRestoreUploads}
                />
                <Label htmlFor="restore-uploads">Restore uploaded files</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRestore} disabled={restoring}>
              {restoring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Restoring...
                </>
              ) : (
                "Restore"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Backup</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this backup?
            </DialogDescription>
          </DialogHeader>
          {backupToDelete && (
            <div className="py-4">
              <p className="font-medium">{backupToDelete.backup_id}</p>
              <div className="flex items-center gap-2 mt-4">
                <Checkbox
                  id="hard-delete"
                  checked={hardDelete}
                  onCheckedChange={setHardDelete}
                />
                <Label htmlFor="hard-delete" className="text-destructive">
                  Permanently delete backup files (cannot be recovered)
                </Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AdminSidebar>
  );
};

export default BackupsPage;
